import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';
import Urls from '../../services/Urls';
import { useRoute, useNavigation } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import DropDownPicker from '../../components/picker/DropDownPicker';
import FilePicker from '../../components/filePicker/filepicker.web';
import DocsComponent from '../../components/docsComponent';
import PopUp from '../../components/popUp/PopupView';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const MyDocs = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [state, setState] = useState({
    property_id: '',
    file_name: '',
    selected_files: [],
  });
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [allPropertiesDocs, setAllPropertiesDocs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [deletedIndex, setDeletedIndex] = useState(null);
  const [allFilteredPropertiesDocs, setAllFilteredPropertiesDocs] = useState([]);

  const getDocxDetailAPICall = () => {
    ajax
      .get(Urls.Properties)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          const temp = data.payload.map((element) => ({
            label: element.name,
            value: element.id,
          }));
          setProperties(temp);
        }
      })
      .catch((error) => console.log('error properties', error));

    ajax
      .get(Urls.GET_ALL_DOCS)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setAllPropertiesDocs(data?.payload);
          setAllFilteredPropertiesDocs(data?.payload);
        }
      })
      .catch((error) => console.log('error all docs', error));
  };

  useEffect(() => {
    getDocxDetailAPICall();
  }, []);

  const _handleChange = (e) => {
    setState((prev) => ({ ...prev, [e.label]: e.value }));
  };

  const _onSave = () => {
    if (saving) return;
    if (!state?.property_id) {
      return showErrorToast('Please select a property');
    }
    if (!state?.selected_files?.length) {
      return showErrorToast('Please select a document to upload');
    }

    setSaving(true);

    const propertyDocs = state.selected_files.map((doc) => ({
      document_name: doc?.name,
      document_url: doc?.openbrixName,
    }));

    const requestBody = {
      file_name: state?.file_name || 'Document',
      property_documents: propertyDocs,
    };

    ajax
      .post(`${Urls.MY_DOCS}/${state?.property_id}`, requestBody)
      .then(({ data }) => {
        setSaving(false);
        if (data.status) {
          showSuccesToast('Documents added successfully!');
          getDocxDetailAPICall();
          setState({
            property_id: '',
            file_name: '',
            selected_files: [],
          });
        } else {
          showErrorToast(data.message || 'Failed to save documents');
        }
      })
      .catch((error) => {
        setSaving(false);
        console.log('error save docs', error);
      });
  };

  const onArchive = (id) => {
    ajax
      .put(Urls.DeleteDoc + id)
      .then(({ data }) => {
        if (data.status) {
          getDocxDetailAPICall();
          setDeletedIndex(null);
        }
      })
      .catch((error) => console.log('error archive doc', error));
  };

  const isSaveDisabled = saving || !state?.selected_files?.length || !state?.property_id;

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb Header */}
      <Text style={styles.breadcrumbText}>Back / My Docs</Text>

      <View style={styles.headerRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.pageTitle}>My Docs</Text>
          <Text style={styles.pageSubtitle}>Your secure document storage for all property-related files.</Text>
        </View>
      </View>

      {/* SECTION 1: ADD DOCUMENT CARD (matching Figma) */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
        <Text style={styles.sectionTitle}>Add Document</Text>

        <View style={[styles.rowGrid, isMobile && styles.rowGridMobile]}>
          {/* Left: Property Dropdown Selector */}
          <View style={[styles.colLeft, isMobile && styles.colLeftMobile]}>
            <DropDownPicker
              items={properties}
              title="Property*"
              placeholder="Search Item"
              defaultValue={state?.property_id}
              onChangeItem={(e) => _handleChange({ value: e.value, label: 'property_id' })}
            />
          </View>

          {/* Right: File Upload Dropzone + Upload Button */}
          <View style={[styles.colRight, isMobile && styles.colRightMobile]}>
            <FilePicker
              onDataSuccess={(data) => {
                setState((prev) => ({
                  ...prev,
                  selected_files: [...(prev.selected_files || []), ...data],
                }));
              }}
              onDelete={(index) => {
                const temp = [...(state.selected_files || [])];
                temp.splice(index, 1);
                setState((prev) => ({ ...prev, selected_files: temp }));
              }}
              files={state?.selected_files}
            />

            <TouchableOpacity
              style={[styles.uploadBtn, isSaveDisabled && styles.uploadBtnDisabled]}
              onPress={_onSave}
              disabled={isSaveDisabled}
              activeOpacity={0.85}
            >
              <Text style={styles.uploadBtnText}>
                {saving ? 'Saving...' : 'Save Document'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Soft Blue/Purple Info Alert Box matching Figma */}
        <View style={styles.infoAlertBox}>
          <Ionicons name="information-circle-outline" size={18} color="#4F46E5" style={{ marginRight: 8 }} />
          <Text style={styles.infoAlertText}>
            Documents are organized by properties . All uploaded files are securely stored and accessible anytime.
          </Text>
        </View>
      </View>

      {/* SECTION 2: MY FILES CARD (matching Figma) */}
      <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile, { marginTop: 24, marginBottom: 40 }]}>
        <View style={styles.filesSectionHeader}>
          <Text style={styles.sectionTitle}>My Files</Text>
          <Text style={styles.foldersCountText}>
            {allFilteredPropertiesDocs.length > 0 ? `${allFilteredPropertiesDocs.length} folders` : '0 folders'}
          </Text>
        </View>

        {allFilteredPropertiesDocs && allFilteredPropertiesDocs.length > 0 ? (
          allFilteredPropertiesDocs.map((val, idx) => (
            <DocsComponent
              key={idx}
              title={val.name}
              listOfDoc={val.property_documents}
              deleteFile={(data) => {
                setDeletedIndex(data);
                setIsVisible(true);
              }}
            />
          ))
        ) : (
          /* Demo Rows matching Figma layout */
          <View>

          </View>
        )}
      </View>

      {/* Archive Popup */}
      <PopUp
        modalVisible={isVisible}
        titleText="Archive file?"
        titleColor="#6C2BD9"
        subTitleColor="black"
        customSubtitle={
          <View style={{ marginTop: 6 }}>
            <Text style={{ fontSize: 13, color: '#374151' }}>
              Are you sure you want to archive this file? It will be moved to document archive.
            </Text>
          </View>
        }
        actionButtons={[
          {
            text: 'Archive',
            width: 100,
            onPress: () => {
              onArchive(deletedIndex);
              setIsVisible(false);
            },
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setIsVisible(false),
          },
        ]}
        closePopup={() => setIsVisible(false)}
      />
    </ScrollView>
  );
};

export default drawerHoc({
  Component: MyDocs,
  showBackButton: true,
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },

  /* MAIN CARD CONTAINER */
  cardContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 16,
    borderRadius: BORDER_RADIUS.md,
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },

  rowGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 16,
  },
  rowGridMobile: {
    flexDirection: 'column',
    gap: 16,
  },

  colLeft: {
    flex: 1,
  },
  colLeftMobile: {
    width: '100%',
  },

  colRight: {
    flex: 1.2,
  },
  colRightMobile: {
    width: '100%',
  },

  uploadBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 42,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  uploadBtnDisabled: {
    backgroundColor: '#9CA3AF',
  },
  uploadBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* INFO ALERT BOX */
  infoAlertBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#DBEAFE',
    borderRadius: BORDER_RADIUS.md,
    padding: 12,
    marginTop: 40,
  },
  infoAlertText: {
    fontSize: 12,
    color: '#1E40AF',
    flex: 1,
    lineHeight: 16,
  },

  /* FILES SECTION HEADER */
  filesSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  foldersCountText: {
    fontSize: 13,
    color: '#9CA3AF',
  },
});
